import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.id === id) || fallbackArticle;
  if (!article) return <NotFound />;

  const relatedArticles = article.relatedIds
    .map((rid) => articles.find((a) => a.id === rid))
    .filter(Boolean) as typeof articles;

  const category = categories.find((c) => c.slug === article.categorySlug);

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), to: '/' },
    { label: article.category, to: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: '91在线吃瓜网',
    },
    publisher: {
      '@type': 'Organization',
      name: '91在线吃瓜网',
      url: 'http://hnqxp01.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://hnqxp01.cn/article/${article.id}`,
    },
    image: article.imageUrl,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '这个爆料的来源是否可靠？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '本爆料内容来源于公开网络渠道和知情人士投稿，附有原始素材截图和视频链接作为证据，读者可自行判断真伪。',
        },
      },
      {
        '@type': 'Question',
        name: '如何查看更多相关内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在文章下方的"相关推荐"区域查看同类爆料，也可以通过页面顶部的分类导航浏览更多频道内容。',
        },
      },
      {
        '@type': 'Question',
        name: '这篇文章会持续更新后续进展吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '91在线吃瓜网会对重大爆料事件进行持续追踪，如有新进展会及时更新本文或发布后续报道，建议关注本站获取最新动态。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | 91在线吃瓜网`}
        description={article.excerpt}
        keywords={`在线吃瓜, 91在线吃瓜网, ${article.category}, ${article.tags.slice(0, 3).join(', ')}`}
        canonicalUrl={`http://hnqxp01.cn/article/${article.id}`}
        ogType="article"
        schemaJson={articleSchema}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <section className="w-full pt-28 pb-0 bg-background-950">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="mb-8">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>
      </section>

      <section className="w-full py-10 md:py-16 bg-background-950">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <div className="lg:w-1/3 shrink-0">
              <div className="inline-flex items-center px-4 py-2 rounded-full border border-primary-400/40 bg-primary-500/5 mb-6">
                <span className="text-primary-400 text-xs font-medium whitespace-nowrap">
                  {t('article.badge.exclusive')}
                </span>
              </div>
              <div className="relative overflow-hidden rounded-3xl">
                <div className="aspect-[4/5] w-full">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    title={`${article.title} - 91在线吃瓜网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="lg:w-2/3 flex flex-col justify-center">
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-foreground-50 leading-tight mb-6">
                {article.title}
              </h1>
              <p className="text-foreground-400 text-sm md:text-base leading-relaxed mb-8">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-4 flex-wrap mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-background-800 text-foreground-400 border border-background-700/50"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
                <span>发布于 {article.publishedAt}</span>
                <span className="text-foreground-600">|</span>
                <span>{(article.views || 0).toLocaleString()} 次围观</span>
                <span className="text-foreground-600">|</span>
                <span>{(article.comments || 0).toLocaleString()} 条评论</span>
              </div>
              <Link
                to="/"
                className="inline-flex px-8 py-3.5 bg-foreground-950 text-background-50 text-sm font-bold rounded-xl hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
              >
                {t('article.btn.watchVideo')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {article.highlights && article.highlights.length > 0 && (
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="lg:w-3/5">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-2 h-2 rounded-full bg-foreground-950"></div>
                  <span className="text-foreground-700 text-sm font-bold uppercase tracking-wider">
                    {t('article.highlight.title')}
                  </span>
                </div>
                <div className="space-y-5">
                  {article.highlights.map((text, idx) => (
                    <p
                      key={idx}
                      className="text-xl md:text-2xl lg:text-3xl font-serif text-foreground-900 leading-relaxed"
                    >
                      &ldquo;{text}&rdquo;
                    </p>
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-8">
                  <span className="text-foreground-800 text-sm font-bold">
                    知情人士
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                    <i className="ri-star-fill"></i>
                    已验证
                  </span>
                </div>
              </div>

              <div className="lg:w-2/5">
                <div className="relative">
                  <div className="aspect-[3/4] w-full rounded-3xl overflow-hidden bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=Black%20and%20white%20portrait%20photography%2C%20person%20in%20silhouette%20looking%20upward%2C%20side%20profile%2C%20dramatic%20lighting%2C%20mysterious%20anonymous%20source%20vibe%2C%20high%20contrast%2C%20clean%20white%20background%2C%20editorial%20style%2C%20cinematic%20composition%2C%20film%20grain%20texture&width=600&height=800&seq=whistleblower-portrait&orientation=portrait"
                      alt="知情人士"
                      title="知情人士爆料"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="w-full py-16 md:py-20 bg-background-950">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col items-center text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/30 bg-transparent mb-6">
              <span className="text-foreground-200 text-xs font-medium whitespace-nowrap">
                {t('article.related.title')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-foreground-50 mb-2">
              {t('article.related.title')}
            </h2>
            <p className="text-foreground-500 text-sm">
              {t('article.related.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedArticles.map((ra) => (
              <ArticleCard
                key={ra.id}
                id={ra.id}
                title={ra.title}
                category={ra.category}
                categorySlug={ra.categorySlug}
                imageUrl={ra.imageUrl}
                imageWidth={ra.imageWidth}
                imageHeight={ra.imageHeight}
                publishedAt={ra.publishedAt}
                isHot={ra.isHot}
                isTop={ra.isTop}
                views={ra.views}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="relative overflow-hidden rounded-2xl md:rounded-3xl">
            <div className="aspect-[21/9] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20scene%20with%20breaking%20news%20concept%2C%20warm%20pink%20and%20golden%20light%20streaks%2C%20dramatic%20shadows%2C%20mysterious%20newsroom%20atmosphere%2C%20editorial%20photography%20style%2C%20artistic%20composition%2C%20film%20grain&width=2100&height=900&seq=cta-article-follow&orientation=landscape"
                alt="追踪后续反转"
                title="追踪后续反转 - 91在线吃瓜网"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-background-950/20"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <p className="text-2xl md:text-4xl lg:text-5xl font-black text-foreground-50 mb-3 leading-tight">
                追踪
                <span className="italic font-light text-foreground-400 mx-2">后续反转</span>
              </p>
              <p className="text-foreground-300 text-sm md:text-base mb-8">
                加入讨论，第一时间获取事件最新进展
              </p>
              <button className="px-10 py-4 bg-foreground-950 text-background-50 text-sm font-bold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap">
                加入讨论
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}